/**
 * Base API client configuration with axios, authentication, and error handling
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useAuthStore } from '../store/authStore';
import { LoggerService } from './LoggerService';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
const API_TIMEOUT = 30000; // 30 seconds

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore.getState();
    const token = authStore.accessToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      LoggerService.debug(`[API Request] Added Authorization header (token length: ${token.length})`);
    } else {
      LoggerService.warn(`[API Request] No token available for ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    // Add request timestamp for debugging
    (config as any).metadata = { startTime: new Date() };
    
    LoggerService.debug(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      data: config.data,
      hasAuth: !!token,
    });
    
    return config;
  },
  (error) => {
    LoggerService.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and logging
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const duration = new Date().getTime() - ((response.config as any).metadata?.startTime?.getTime() || 0);
    
    LoggerService.debug(`[API Response] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      duration: `${duration}ms`,
      data: response.data,
    });
    
    return response;
  },
  (error: AxiosError) => {
    const authStore = useAuthStore.getState();
    
    LoggerService.error('[API Response Error]', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      data: error.response?.data,
    });
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      LoggerService.warn('[API] Authentication failed, logging out user');
      authStore.logout();
      window.location.href = '/login';
      return Promise.reject(new Error('Authentication failed. Please log in again.'));
    }
    
    // Handle authorization errors
    if (error.response?.status === 403) {
      return Promise.reject(new Error('You do not have permission to access this resource.'));
    }
    
    // Handle validation errors
    if (error.response?.status === 422) {
      const validationErrors = (error.response.data as any)?.detail || [];
      const errorMessage = Array.isArray(validationErrors) 
        ? validationErrors.map((err: any) => err.msg || err.message).join(', ')
        : 'Validation failed';
      return Promise.reject(new Error(errorMessage));
    }
    
    // Handle server errors
    if (error.response && error.response.status >= 500) {
      return Promise.reject(new Error('Server error. Please try again later.'));
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('Request timeout. Please check your connection.'));
    }
    
    if (!error.response) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }
    
    // Default error handling
    const errorMessage = (error.response?.data as any)?.detail || error.message || 'An unexpected error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

// Generic API methods
export const apiGet = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.get<T>(url, config);
  return response.data;
};

export const apiPost = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
};

export const apiPut = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
};

export const apiPatch = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
};

export const apiDelete = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
};

// File upload helper
export const apiUpload = async <T = any>(
  url: string, 
  file: File, 
  onProgress?: (progressEvent: any) => void
): Promise<T> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress,
  });
  
  return response.data;
};

// Bulk request helper
export const apiBulk = async <T = any>(requests: Array<() => Promise<any>>): Promise<T[]> => {
  const results = await Promise.allSettled(requests.map(req => req()));
  
  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      LoggerService.error(`[API Bulk Request ${index}] Failed:`, result.reason);
      throw result.reason;
    }
  });
};

// Health check
export const apiHealthCheck = async (): Promise<{ status: string; version: string }> => {
  return apiGet('/health');
};

// Utility types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T = any> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Export the configured axios instance for advanced usage
export { apiClient };
export default apiClient;